import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { Navigate, createFileRoute } from '@tanstack/react-router'
import { Check, Copy } from 'lucide-react'
import { ErrorApi } from '@/api/errores'
import { copiarAlPortapapeles } from '@/lib/portapapeles'
import { formatearHora } from '@/lib/fecha'
import { Boton } from '@/ui/Boton'
import { CodigoQR } from '@/consola/CodigoQR'
import {
  confirmarCodigo,
  pedirPaso,
  sesionConsolaActual,
  suscribirseConsola,
} from '@/dominio/consola/sesion'

/**
 * Entrada a la consola (`docs/09` §3.1): correo y credencial, y después el
 * código de seis dígitos. Si el operador todavía no tiene segundo factor, el
 * segundo paso es enrolarlo: QR y la clave para teclear a mano.
 *
 * La credencial, el testigo de paso y el secreto viven solo en el estado de
 * esta pantalla. Nada de eso se guarda.
 */

interface BusquedaEntrar {
  volver?: string
}

/**
 * Solo se vuelve a una pantalla **de la consola**. Aceptar cualquier destino
 * convierte la entrada en un redirector: un enlace a
 * `/consola/entrar?volver=https://otro.sitio` llevaría al operador, recién
 * autenticado, a donde quiera quien lo mandó.
 */
function destinoSeguro(valor: unknown): string | undefined {
  if (typeof valor !== 'string') return undefined
  if (!valor.startsWith('/consola') || valor.startsWith('/consola/entrar') || valor.startsWith('//')) return undefined
  return valor
}

export const Route = createFileRoute('/consola/entrar')({
  validateSearch: (busqueda: Record<string, unknown>): BusquedaEntrar => ({
    volver: destinoSeguro(busqueda.volver),
  }),
  component: PantallaEntrar,
})

/**
 * Lo que no es un 401 no es culpa de lo tecleado. Decir «no coincide» ante un
 * servidor caído manda al operador a cambiar una credencial que estaba bien.
 */
function mensajeDeFalloTecnico(fallo: unknown): string {
  if (!(fallo instanceof ErrorApi)) return 'No se pudo completar la entrada. Vuelve a intentarlo.'
  if (fallo.clase === 'red') return 'No hay conexión con el servidor.'
  if (fallo.estado === 404) return 'El servidor todavía no tiene la entrada de la consola.'
  return `El servidor no pudo atender la entrada (HTTP ${fallo.estado}). Vuelve a intentarlo en un momento.`
}

/** El testigo de paso vive cinco minutos en el servidor. */
const VIDA_DEL_PASO_MS = 5 * 60_000

type Paso =
  | { tipo: 'credencial' }
  | { tipo: 'codigo'; testigoPaso: string; vence: Date }
  | { tipo: 'enrolar'; testigoPaso: string; vence: Date; otpauth: string; secreto: string }

function PantallaEntrar() {
  const sesion = useSyncExternalStore(suscribirseConsola, sesionConsolaActual, () => null)
  const { volver } = Route.useSearch()

  const [paso, setPaso] = useState<Paso>({ tipo: 'credencial' })
  const [aviso, setAviso] = useState<string | null>(null)

  // El paso caduca en el servidor a los cinco minutos. Se vuelve al principio a
  // esa hora en vez de dejar que el operador teclee un código contra un testigo
  // muerto y reciba un «no coincide» que no es verdad.
  useEffect(() => {
    if (paso.tipo === 'credencial') return
    const restante = paso.vence.getTime() - Date.now()
    const temporizador = setTimeout(() => {
      setPaso({ tipo: 'credencial' })
      setAviso('Pasaron cinco minutos sin completar la entrada. Vuelve a poner la credencial.')
    }, Math.max(0, restante))
    return () => clearTimeout(temporizador)
  }, [paso])

  // Con la sesión recién abierta se sale de aquí solo: la guarda de la consola
  // ya la ve, y una segunda navegación a mano sería una carrera con esta.
  // `volver` es una ruta ya resuelta de la consola (la validó `destinoSeguro`),
  // no una plantilla con parámetros; el tipo del árbol no puede saberlo.
  if (sesion !== null) return <Navigate to={(volver ?? '/consola') as '/consola'} replace />

  return (
    <div className="flex min-h-screen flex-col bg-gris-100">
      {/* La franja oscura es la de la consola: esta no es la entrada del portal. */}
      <div className="flex h-8 shrink-0 items-center justify-center bg-gris-900 text-etiqueta font-medium uppercase tracking-wide text-gris-300">
        Consola de la plataforma
      </div>

      <div className="flex flex-1 items-center justify-center px-4 py-8">
        {paso.tipo === 'credencial' ? (
          <PasoCredencial
            aviso={aviso}
            onPaso={(siguiente) => {
              setAviso(null)
              const vence = new Date(Date.now() + VIDA_DEL_PASO_MS)
              setPaso(
                siguiente.paso === 'enrolar'
                  ? {
                      tipo: 'enrolar',
                      testigoPaso: siguiente.testigoPaso,
                      vence,
                      otpauth: siguiente.otpauth,
                      secreto: siguiente.secreto,
                    }
                  : { tipo: 'codigo', testigoPaso: siguiente.testigoPaso, vence },
              )
            }}
          />
        ) : (
          <PasoCodigo
            paso={paso}
            onVolver={() => {
              setAviso(null)
              setPaso({ tipo: 'credencial' })
            }}
          />
        )}
      </div>
    </div>
  )
}

function PasoCredencial({
  aviso,
  onPaso,
}: {
  aviso: string | null
  onPaso: (paso: Awaited<ReturnType<typeof pedirPaso>>) => void
}) {
  const [email, setEmail] = useState('')
  const [credencial, setCredencial] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault()
    setError(null)
    setEnviando(true)
    try {
      const siguiente = await pedirPaso(email.trim(), credencial)
      setCredencial('')
      onPaso(siguiente)
    } catch (fallo) {
      // Un solo mensaje para correo inexistente, credencial mala, cuenta
      // inactiva y «no es operador», igual que el servidor. Distinguirlos
      // convertiría esta pantalla en un comprobador de quién opera la plataforma.
      setError(
        fallo instanceof ErrorApi && fallo.clase === 'autenticacion'
          ? 'El correo o la credencial no coinciden con un operador de la plataforma.'
          : mensajeDeFalloTecnico(fallo),
      )
      setCredencial('')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <form
      onSubmit={(e) => void enviar(e)}
      className="flex w-full max-w-[380px] flex-col gap-4 rounded-md border border-gris-300 bg-white p-6 shadow-flotante"
    >
      <div className="flex flex-col gap-1">
        <h1 className="text-pantalla text-gris-900">Entrar a la consola</h1>
        <p className="text-etiqueta text-gris-600">
          El mismo correo y la misma credencial del portal. Después se pide el código de tu app de autenticación.
        </p>
      </div>

      {aviso !== null ? (
        <p role="status" className="rounded-md border border-gris-300 bg-ambar-tenue px-2 py-1.5 text-etiqueta text-gris-900">
          {aviso}
        </p>
      ) : null}

      <label className="flex flex-col gap-1">
        <span className="text-etiqueta font-medium text-gris-600">Correo</span>
        <input
          type="email"
          required
          autoFocus
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-[var(--alto-control)] rounded-md border border-gris-500 px-2 text-cuerpo"
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-etiqueta font-medium text-gris-600">Credencial</span>
        <input
          type="password"
          required
          autoComplete="current-password"
          value={credencial}
          onChange={(e) => setCredencial(e.target.value)}
          className="h-[var(--alto-control)] rounded-md border border-gris-500 px-2 text-cuerpo"
        />
      </label>

      {error !== null ? (
        <p role="alert" className="rounded-md border border-gris-300 border-l-[3px] border-l-rojo bg-rojo-tenue px-2 py-1.5 text-etiqueta text-gris-900">
          {error}
        </p>
      ) : null}

      <Boton type="submit" variante="primario" disabled={enviando}>
        {enviando ? 'Comprobando…' : 'Continuar'}
      </Boton>
    </form>
  )
}

function PasoCodigo({
  paso,
  onVolver,
}: {
  paso: Extract<Paso, { tipo: 'codigo' | 'enrolar' }>
  onVolver: () => void
}) {
  const [codigo, setCodigo] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  const campo = useRef<HTMLInputElement>(null)
  const enviado = useRef<string | null>(null)

  async function enviar(valor: string) {
    // El mismo código no se manda dos veces: el envío automático al completar
    // los seis dígitos y un Enter a la vez no deben gastar dos intentos.
    if (enviando || enviado.current === valor) return
    enviado.current = valor
    setError(null)
    setEnviando(true)
    try {
      await confirmarCodigo(paso.testigoPaso, valor)
    } catch (fallo) {
      setError(
        fallo instanceof ErrorApi && fallo.clase === 'autenticacion'
          ? 'El código no coincide. Si la hora del teléfono no está en automático, el código sale corrido.'
          : mensajeDeFalloTecnico(fallo),
      )
      setCodigo('')
      enviado.current = null
      campo.current?.focus()
    } finally {
      setEnviando(false)
    }
  }

  const enrolando = paso.tipo === 'enrolar'

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (codigo.length === 6) void enviar(codigo)
      }}
      className="flex w-full max-w-[420px] flex-col gap-4 rounded-md border border-gris-300 bg-white p-6 shadow-flotante"
    >
      <div className="flex flex-col gap-1">
        <h1 className="text-pantalla text-gris-900">{enrolando ? 'Activa el segundo factor' : 'Código de verificación'}</h1>
        <p className="text-etiqueta text-gris-600">
          {enrolando
            ? 'La consola exige un segundo factor. Se activa una vez, con cualquier app de autenticación.'
            : 'Escribe el código de seis dígitos de tu app de autenticación.'}
        </p>
      </div>

      {paso.tipo === 'enrolar' ? <Enrolamiento otpauth={paso.otpauth} secreto={paso.secreto} /> : null}

      <label className="flex flex-col gap-1">
        <span className="text-etiqueta font-medium text-gris-600">
          {enrolando ? 'Código que muestra la app' : 'Código'}
        </span>
        {/*
          El foco entra aquí solo y el código se envía al completar los seis
          dígitos (docs/07, Autenticación): nadie debería tener que pulsar un
          botón en este paso.
        */}
        <input
          ref={campo}
          autoFocus={!enrolando}
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          value={codigo}
          // `readOnly` y no `disabled`: un campo deshabilitado no admite el
          // foco, y tras un código fallido el foco tiene que volver aquí solo.
          readOnly={enviando}
          onChange={(e) => {
            const soloDigitos = e.target.value.replace(/\D/g, '').slice(0, 6)
            setCodigo(soloDigitos)
            if (soloDigitos.length === 6) void enviar(soloDigitos)
          }}
          aria-describedby="vence-paso"
          className="mono h-[var(--alto-control)] rounded-md border border-gris-500 px-2 text-center text-[20px] tracking-[0.4em]"
        />
        <span id="vence-paso" className="text-etiqueta text-gris-600">
          Este paso vence a las <span className="mono">{formatearHora(paso.vence).slice(0, 5)}</span>.
        </span>
      </label>

      {error !== null ? (
        <p role="alert" className="rounded-md border border-gris-300 border-l-[3px] border-l-rojo bg-rojo-tenue px-2 py-1.5 text-etiqueta text-gris-900">
          {error}
        </p>
      ) : null}

      <div className="flex items-center justify-between gap-2">
        <button type="button" onClick={onVolver} className="text-etiqueta text-gris-600 underline">
          Empezar de nuevo
        </button>
        <Boton type="submit" variante="primario" disabled={enviando || codigo.length !== 6}>
          {enviando ? 'Comprobando…' : enrolando ? 'Activar y entrar' : 'Entrar'}
        </Boton>
      </div>
    </form>
  )
}

/** Agrupa la clave de cuatro en cuatro: se teclea a mano sin perderse. */
function agrupar(secreto: string): string {
  return secreto.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim()
}

function Enrolamiento({ otpauth, secreto }: { otpauth: string; secreto: string }) {
  const [copiado, setCopiado] = useState(false)

  return (
    <div className="flex flex-col gap-3 rounded-md border border-gris-300 bg-gris-50 p-4">
      <ol className="flex list-decimal flex-col gap-1 pl-4 text-cuerpo text-gris-700">
        <li>Abre tu app de autenticación y escanea este código.</li>
        <li>Si no puedes escanearlo, añade la cuenta a mano con la clave de abajo.</li>
        <li>Escribe el código de seis dígitos que te muestra la app.</li>
      </ol>

      <div className="flex justify-center">
        <CodigoQR texto={otpauth} etiqueta="Código QR para añadir la consola a la app de autenticación" />
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-etiqueta text-gris-600">Clave para teclear a mano</span>
        <span className="flex items-center gap-2">
          <span className="mono break-all text-gris-900">{agrupar(secreto)}</span>
          <button
            type="button"
            onClick={() => {
              void copiarAlPortapapeles(secreto.replace(/\s/g, '')).then((exito) => {
                if (!exito) return
                setCopiado(true)
                setTimeout(() => setCopiado(false), 1500)
              })
            }}
            aria-label={copiado ? 'Clave copiada' : 'Copiar la clave'}
            className="inline-flex size-6 shrink-0 items-center justify-center rounded-md text-gris-600 hover:bg-gris-100 hover:text-gris-900"
          >
            {copiado ? <Check className="size-4 text-verde" aria-hidden="true" /> : <Copy className="size-4" aria-hidden="true" />}
          </button>
        </span>
        <span className="text-etiqueta text-gris-600">Tipo: basada en tiempo · 6 dígitos · cada 30 segundos.</span>
      </div>

      <p className="text-etiqueta text-gris-600">
        Si sales sin terminar, la próxima entrada trae una clave nueva y esta deja de servir: borra la cuenta vieja de
        la app antes de añadir la nueva.
      </p>
    </div>
  )
}
